import React from "react";
import { cn } from "@/lib/utils";
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from "react-hook-form";

interface NativeSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "name"> {
  control: Control<TFieldValues>;
  label: string;
  options: { id: string | number; name: string }[];
  name: TName;
  containerStyles?: string;
  onSelectChange?: (option: { id: string | number; name: string }) => void;
  handleInputChange?: () => void;
  inputStyles?: string;
  placeholder?: string;
  autoComplete?: string;
  showErrorMsg?: boolean;
}

export default function NativeSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  options,
  name,
  containerStyles,
  onSelectChange,
  handleInputChange,
  inputStyles,
  placeholder = "Select an option",
  autoComplete,
  showErrorMsg = false,
  ...props
}: NativeSelectProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const selectedOption = options.find(
      (option) => option.id.toString() === selectedValue,
    );

    if (selectedOption) {
      if (onSelectChange) {
        onSelectChange(selectedOption);
      } else {
        field.onChange(selectedOption.id.toString());
      }
    } else {
      field.onChange(selectedValue);
    }
    handleInputChange?.();
  };

  // Find the selected option to get its id for the select value
  // Handle different field value types
  let selectValue = "";

  if (field.value) {
    if (typeof field.value === "string") {
      // First try to find by id (most common case after fix)
      let selectedOption = options.find(
        (option) => option.id.toString() === field.value,
      );

      // If not found by id, try to find by name (for backward compatibility)
      if (!selectedOption) {
        selectedOption = options.find((option) => option.name === field.value);
      }

      selectValue = selectedOption ? selectedOption.id.toString() : "";
    } else if (typeof field.value === "object") {
      // Complex object - try different properties
      const countryName =
        field.value.country || field.value.name || field.value;
      const selectedOption = options.find(
        (option) => option.name === countryName,
      );
      selectValue = selectedOption ? selectedOption.id.toString() : "";
    }
  }

  return (
    <div className={cn("relative w-full flex-1", containerStyles)}>
      <label
        htmlFor={name}
        className={cn(
          "pointer-events-none absolute top-1/4 left-3 z-20 max-w-[calc(100%-2rem)] -translate-y-1/2 transform truncate text-xs transition-all",
          error?.message && showErrorMsg ? "text-red-400" : "text-gray-400",
        )}
      >
        {error?.message && showErrorMsg ? error.message : label}
      </label>
      <select
        {...props}
        id={name}
        name={name}
        value={selectValue}
        onChange={handleSelectChange}
        autoComplete={autoComplete}
        className={cn(
          "h-input relative z-10 w-full border-0 bg-white px-3 pt-5 pb-1 text-base ring-1 sm:text-sm",
          "focus:ring-2 focus:ring-black focus:outline-hidden focus:ring-inset",
          error ? "shadow-error" : "ring-gray-300",
          inputStyles,
        )}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
}
